import CustomTooltip from '../../shared/CustomTooltip';
import FormUpload from '../../shared/FormUpload';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';

import { PhotoCamera } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    List,
    ListItem,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
    useGetProfileCompanyInfoQuery,
    useGetProfileDetailsQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import RoleChip from '../../shared/RoleChip';
import ProfilePicture from './ProfilePicture';
import { useEditProfileNavigation } from './useEditProfileNavigation';
import ProfilePictureModal from './Form/Profile Picture/ProfilePictureModal';
import { Media } from '../../types/user';
import { getMandatoryProfileSetupMissingItems } from './profileSetupBanner';
import { useProfileInlineUx } from './ProfileInlineUxContext';

const formatMemberDate = (value: unknown) => {
    if (value == null || value === '') return null;
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

/** Date + time for profile “last logged in” on narrow screens */
const formatLastLoginAtDisplay = (value: unknown) => {
    if (value == null || value === '') return null;
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

function clonePhotoSnapshot<T>(value: T): T {
    try {
        return structuredClone(value);
    } catch {
        return JSON.parse(JSON.stringify(value)) as T;
    }
}

type ProfilePhotoPickForm = { profilePhotoPick: Media[] };

/** Isolated RHF scope so FormUpload does not register on the main profile form. */
function ProfilePhotoDropZone({
    onFileChosen,
}: {
    onFileChosen: (file: File) => void;
}) {
    const methods = useForm<ProfilePhotoPickForm>({
        defaultValues: { profilePhotoPick: [] },
    });

    const onUpload = useCallback(
        async (files: File[]): Promise<Media[] | undefined> => {
            const file = files[0];
            if (file) {
                onFileChosen(file);
                return [];
            }
            return undefined;
        },
        [onFileChosen],
    );

    return (
        <FormProvider {...methods}>
            <FormUpload
                name='profilePhotoPick'
                control={methods.control}
                onUpload={onUpload}
                showFiles={false}
                compact
                validation={{
                    acceptedTypes: {
                        'image/png': ['.png', '.jpg', '.jpeg'],
                    },
                    maxFileSize: 5 * 1024 * 1024,
                    maxFiles: 1,
                }}
                uploadText='Click to browse files or drag and drop'
                helperText='PNG or JPG up to 5MB'
                hasLightBox={false}
            />
        </FormProvider>
    );
}

const ProfileSummary = ({
    contractorCompany,
    highlightMissingRequired = false,
}: {
    contractorCompany: boolean;
    /** Highlight mandatory missing fields after the action-required banner click */
    highlightMissingRequired?: boolean;
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();
    const loggedInUser = UserRole();
    const { canShowEdit } = useEditProfileNavigation(contractorCompany);
    const { setValue, getValues, control } = useFormContext();
    const profilePictureFromForm = useWatch({
        control,
        name: 'profilePicture',
    }) as Media | undefined;
    const {
        saveInlineEdit,
        profilePhotoInlineControllerRef,
        tryBeginPhotoEdit,
        requestUnsavedOpenPhoto,
    } = useProfileInlineUx();

    const { data: profileDetails, refetch: refetchProfileDetails } =
        useGetProfileDetailsQuery('/profile');
    const { data: companyDetails } = useGetProfileCompanyInfoQuery(
        `${loggedInUser?.role.slice(0, 5)}s/${loggedInUser?.company}`,
    );

    const photoSnapshotRef = useRef<unknown>(undefined);
    const mobileFileInputRef = useRef<HTMLInputElement>(null);
    const [photoEditActive, setPhotoEditActive] = useState(false);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [pickedPhoto, setPickedPhoto] = useState<File | undefined>();

    const closePhotoModal = useCallback(() => {
        setPhotoModalOpen(false);
        setPickedPhoto(undefined);
    }, []);

    const handlePhotoSaved = useCallback(
        (media: Media) => {
            setValue('profilePicture' as never, media as never, {
                shouldDirty: true,
                shouldValidate: true,
            });
        },
        [setValue],
    );

    const cancelPhotoEdit = useCallback(() => {
        setValue('profilePicture' as never, photoSnapshotRef.current as never, {
            shouldDirty: false,
            shouldValidate: true,
        });
        setPhotoEditActive(false);
        closePhotoModal();
        setPickedPhoto(undefined);
        void refetchProfileDetails();
    }, [setValue, closePhotoModal, refetchProfileDetails]);

    const savePhotoEdit = useCallback(() => {
        saveInlineEdit();
        setPhotoEditActive(false);
        closePhotoModal();
        setPickedPhoto(undefined);
    }, [saveInlineEdit, closePhotoModal]);

    const onDropZoneFile = useCallback(
        (file: File) => {
            requestUnsavedOpenPhoto(file);
        },
        [requestUnsavedOpenPhoto],
    );

    const openMobilePhotoPicker = useCallback(() => {
        mobileFileInputRef.current?.click();
    }, []);

    const onMobilePhotoInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) {
                requestUnsavedOpenPhoto(file);
            }
        },
        [requestUnsavedOpenPhoto],
    );

    const completeOpenPhoto = useCallback(
        (file?: File) => {
            if (!photoEditActive) {
                photoSnapshotRef.current = clonePhotoSnapshot(
                    getValues('profilePicture' as never),
                );
                setPhotoEditActive(true);
            }
            if (file) {
                setPickedPhoto(file);
                setPhotoModalOpen(true);
            }
        },
        [photoEditActive, getValues],
    );

    const endPhotoEditAfterSuccessfulSubmit = useCallback(() => {
        setPhotoEditActive(false);
        closePhotoModal();
        setPickedPhoto(undefined);
    }, [closePhotoModal]);

    useLayoutEffect(() => {
        profilePhotoInlineControllerRef.current = {
            isPhotoEditActive: photoEditActive,
            completeOpenPhoto,
            cancelPhotoEdit,
            endPhotoEditAfterSuccessfulSubmit,
            ...(isMobile
                ? { openNativePhotoPicker: openMobilePhotoPicker }
                : {}),
        };
    }, [
        profilePhotoInlineControllerRef,
        photoEditActive,
        completeOpenPhoto,
        cancelPhotoEdit,
        endPhotoEditAfterSuccessfulSubmit,
        isMobile,
        openMobilePhotoPicker,
    ]);

    const canEditProfilePhoto =
        !loggedInUser?.role?.includes('company') &&
        loggedInUser?.role !== 'admin' &&
        !contractorCompany;

    const profileSetupMissing = (() => {
        if (!highlightMissingRequired) return [];
        const apiPicture = profileDetails?.profilePicture as
            | { id?: string; src?: string; valid?: boolean }
            | undefined;
        const formHasReplacement =
            Boolean(profilePictureFromForm?.id) &&
            profilePictureFromForm?.id !== apiPicture?.id;
        if (formHasReplacement) return [];
        return getMandatoryProfileSetupMissingItems(
            { profilePicture: apiPicture ?? profilePictureFromForm },
            loggedInUser?.role,
        );
    })();

    const showProfilePictureRequiredHighlight = profileSetupMissing.length > 0;

    const memberSinceRaw =
        (
            profileDetails as
                | { createdAt?: string; joinedAt?: string }
                | undefined
        )?.createdAt ??
        (
            profileDetails as
                | { createdAt?: string; joinedAt?: string }
                | undefined
        )?.joinedAt;
    const memberSince = formatMemberDate(memberSinceRaw);

    const profileLoginMeta = profileDetails as
        | {
              lastLoginAt?: string;
              last_login_at?: string;
              lastLoggedInAt?: string;
          }
        | null
        | undefined;
    const lastLoginRaw =
        profileLoginMeta?.lastLoginAt ??
        profileLoginMeta?.last_login_at ??
        profileLoginMeta?.lastLoggedInAt;
    const lastLoggedInDate = formatMemberDate(lastLoginRaw);
    const lastLoggedInAtWithTime = formatLastLoginAtDisplay(lastLoginRaw);

    const isActive = profileDetails?.isActive === true;

    const accountRowStackProps = {
        direction: 'row' as const,
        alignItems: 'center' as const,
        justifyContent: {
            xs: 'space-between',
            sm: 'flex-start',
        } as const,
        flexWrap: { xs: 'nowrap', sm: 'wrap' } as const,
        gap: { xs: 0, sm: 1.5 },
        sx: { width: '100%' },
    };

    const statusChip = (
        <Chip
            label={isActive ? 'Enabled' : 'Disabled'}
            size='small'
            sx={{
                flexShrink: 0,
                fontWeight: 600,
                bgcolor: isActive
                    ? theme.palette.textAccent.contrastText
                    : 'none',
                color: isActive
                    ? theme.palette.textAccent.main
                    : 'text.secondary',
                borderColor: theme.palette.textAccent.main,
            }}
        />
    );

    const profileAccountRows = (
        <Stack
            spacing={1.75}
            sx={{
                minWidth: { xs: 0, sm: 200 },
                maxWidth: { xs: 'none', sm: 280 },
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
            }}
        >
            <Stack {...accountRowStackProps}>
                <Typography
                    variant='body2'
                    component='span'
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        flexShrink: 0,
                    }}
                >
                    Account Status
                </Typography>
                {statusChip}
            </Stack>
            {lastLoggedInDate ? (
                <Stack {...accountRowStackProps}>
                    <Typography
                        variant='body2'
                        component='span'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            flexShrink: 0,
                        }}
                    >
                        Last logged in at
                    </Typography>
                    <Typography
                        variant='body2'
                        component='span'
                        fontWeight={600}
                        sx={{
                            color: 'text.primary',
                            textAlign: { xs: 'right', sm: 'left' },
                            minWidth: 0,
                        }}
                    >
                        {isMobile ? lastLoggedInAtWithTime : lastLoggedInDate}
                    </Typography>
                </Stack>
            ) : null}
            {memberSince ? (
                <Stack {...accountRowStackProps}>
                    <Typography
                        variant='body2'
                        component='span'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            flexShrink: 0,
                        }}
                    >
                        Member since
                    </Typography>
                    <Typography
                        variant='body2'
                        component='span'
                        fontWeight={600}
                        sx={{
                            color: 'text.primary',
                            textAlign: { xs: 'right', sm: 'left' },
                            minWidth: 0,
                        }}
                    >
                        {memberSince}
                    </Typography>
                </Stack>
            ) : null}
        </Stack>
    );

    const renderDesktopAccountAside = (pinEnd: boolean) => (
        <Stack
            direction='row'
            alignItems='center'
            spacing={2.5}
            sx={{
                flexShrink: 0,
                ...(pinEnd ? { ml: 'auto', pl: 1 } : {}),
            }}
        >
            <div
                style={{
                    width: '1px',
                    alignSelf: 'stretch',
                    backgroundColor: theme.palette.custom.modalActionBorder,
                }}
            />

            {profileAccountRows}
        </Stack>
    );

    const nameCompanyEmailBlock = (
        <Stack
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            spacing={0.5}
            sx={{
                textAlign: { xs: 'center', sm: 'left' },
                minWidth: 0,
            }}
        >
            {showProfilePictureRequiredHighlight && (
                <Typography
                    variant='subtitle2'
                    sx={{
                        color: 'red.main',
                        fontWeight: 600,
                        lineHeight: 1.3,
                    }}
                >
                    Profile picture required
                </Typography>
            )}
            <Typography
                variant='h5'
                sx={{
                    fontWeight: 700,
                    color: 'primary.dark',
                }}
            >
                {profileDetails?.firstName} {profileDetails?.lastName}
            </Typography>
            <Stack
                direction='row'
                alignItems='center'
                flexWrap='wrap'
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                gap={1}
            >
                <Typography variant='body1' color='text.secondary'>
                    {companyDetails?.name}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                    -
                </Typography>
                <RoleChip user={profileDetails} />
            </Stack>
            <Typography variant='body2' color='text.secondary'>
                {profileDetails?.email}
            </Typography>
        </Stack>
    );

    const avatarWithControls = (
        <Box
            id='profile-setup-photo'
            sx={{
                position: 'relative',
                flexShrink: 0,
                borderRadius: '50%',
                ...(showProfilePictureRequiredHighlight
                    ? {
                          p: '3px',
                          backgroundColor: alpha(theme.palette.red.main, 0.08),
                          outline: `2px dashed ${theme.palette.red.main}`,
                          outlineOffset: 2,
                      }
                    : {}),
            }}
        >
            <ProfilePicture
                profileDetails={profileDetails}
                src={
                    profilePictureFromForm?.src ??
                    profileDetails?.profilePicture?.src
                }
                sx={{
                    width: { xs: 112, sm: 140 },
                    height: { xs: 112, sm: 140 },
                    fontSize: '2rem',
                }}
            />
            {canShowEdit && canEditProfilePhoto && !photoEditActive && (
                <CustomTooltip
                    title={
                        <Typography>
                            <List dense disablePadding>
                                <ListItem
                                    sx={{
                                        py: 0.25,
                                        display: 'block',
                                    }}
                                >
                                    Your profile picture may be used for facial
                                    recognition and is required for some
                                    experiences.
                                </ListItem>
                            </List>
                        </Typography>
                    }
                    placement='right'
                >
                    <Box
                        component='span'
                        sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            lineHeight: 0,
                        }}
                    >
                        <IconButton
                            size='small'
                            type='button'
                            onClick={tryBeginPhotoEdit}
                            sx={{
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 1,
                                '&:hover': {
                                    bgcolor: 'background.paper',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                            aria-label='Edit profile photo'
                        >
                            <PhotoCamera
                                sx={{
                                    fontSize: 18,
                                    color: 'text.secondary',
                                }}
                            />
                        </IconButton>
                    </Box>
                </CustomTooltip>
            )}
            {photoEditActive &&
                canShowEdit &&
                canEditProfilePhoto &&
                (isMobile ? (
                    <Box
                        component='span'
                        sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            lineHeight: 0,
                        }}
                    >
                        <IconButton
                            size='small'
                            type='button'
                            onClick={openMobilePhotoPicker}
                            sx={{
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 1,
                                '&:hover': {
                                    bgcolor: 'background.paper',
                                },
                            }}
                            aria-label='Change profile photo'
                        >
                            <PhotoCamera
                                sx={{
                                    fontSize: 18,
                                    color: 'text.secondary',
                                }}
                            />
                        </IconButton>
                    </Box>
                ) : (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <PhotoCamera
                            sx={{ fontSize: 18, color: 'text.secondary' }}
                        />
                    </Box>
                ))}
            {canShowEdit && canEditProfilePhoto && (
                <ProfilePictureModal
                    open={photoModalOpen}
                    onClose={closePhotoModal}
                    onSubmit={handlePhotoSaved}
                    photo={pickedPhoto}
                />
            )}
        </Box>
    );

    const inPhotoEditLayout =
        photoEditActive && canShowEdit && canEditProfilePhoto;

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                overflow: 'hidden',
            }}
        >
            {photoEditActive && canShowEdit && canEditProfilePhoto && (
                <Stack
                    direction='row'
                    alignItems='flex-start'
                    spacing={2}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        pb: { xs: 1.5, sm: 2 },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box
                        component={isMobile ? 'button' : 'div'}
                        type={isMobile ? 'button' : undefined}
                        onClick={isMobile ? openMobilePhotoPicker : undefined}
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            bgcolor: (t) =>
                                t.palette.mode === 'dark'
                                    ? 'action.selected'
                                    : alpha(t.palette.primary.main, 0.12),
                            color: 'primary.main',
                            border: 'none',
                            p: 0,
                            cursor: isMobile ? 'pointer' : 'default',
                        }}
                        aria-label={
                            isMobile ? 'Change profile photo' : undefined
                        }
                    >
                        <PhotoCamera sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography
                            component='h2'
                            variant='h6'
                            sx={{
                                fontWeight: 700,
                                color: 'primary.dark',
                                lineHeight: 1.3,
                            }}
                        >
                            Profile photo
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            Upload a clear photo of your face. PNG or JPG up to
                            5MB.
                        </Typography>
                    </Box>
                </Stack>
            )}

            <Box
                sx={{
                    p: { xs: 2, sm: 3 },
                    width: '100%',
                    display: 'flex',
                    gap: 3,
                    ...(inPhotoEditLayout && !isMobile
                        ? {
                              flexDirection: 'row',
                              alignItems: 'center',
                          }
                        : {
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'center', sm: 'center' },
                          }),
                }}
            >
                {inPhotoEditLayout && !isMobile ? (
                    <Stack
                        direction='row'
                        alignItems='center'
                        spacing={3}
                        sx={{ width: '100%', minWidth: 0 }}
                    >
                        {avatarWithControls}
                        {nameCompanyEmailBlock}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: { xs: '100%', sm: 280 },
                                width: '100%',
                                maxWidth: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'center',
                            }}
                        >
                            <ProfilePhotoDropZone
                                onFileChosen={onDropZoneFile}
                            />
                        </Box>
                        {renderDesktopAccountAside(false)}
                    </Stack>
                ) : inPhotoEditLayout && isMobile ? (
                    <>
                        <Stack
                            spacing={3}
                            alignItems='stretch'
                            sx={{ width: '100%' }}
                        >
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                            >
                                {avatarWithControls}
                                {nameCompanyEmailBlock}
                            </Stack>
                        </Stack>
                        <Divider sx={{ width: '100%' }} />
                        {profileAccountRows}
                    </>
                ) : (
                    <>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'center', sm: 'center' }}
                            spacing={3}
                            sx={{
                                flexShrink: 0,
                                minWidth: 0,
                                maxWidth: '100%',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            {avatarWithControls}
                            {nameCompanyEmailBlock}
                        </Stack>
                        {!isMobile && renderDesktopAccountAside(true)}
                        {isMobile && (
                            <>
                                <Divider sx={{ width: '100%' }} />
                                {profileAccountRows}
                            </>
                        )}
                    </>
                )}
            </Box>

            {photoEditActive && canShowEdit && canEditProfilePhoto && (
                <Stack
                    direction='row'
                    justifyContent='flex-end'
                    alignItems='center'
                    spacing={1}
                    flexWrap='wrap'
                    useFlexGap
                    sx={{
                        p: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 2 },
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        type='button'
                        variant='outlined'
                        size='small'
                        onClick={cancelPhotoEdit}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'divider',
                            color: 'text.primary',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type='button'
                        variant='contained'
                        size='small'
                        onClick={savePhotoEdit}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Save
                    </Button>
                </Stack>
            )}

            {isMobile && canEditProfilePhoto && (
                <input
                    ref={mobileFileInputRef}
                    type='file'
                    accept='image/png,image/jpeg,image/jpg'
                    hidden
                    onChange={onMobilePhotoInputChange}
                />
            )}
        </Paper>
    );
};

export default ProfileSummary;
